import React, { useState, useEffect } from 'react';
import '../styles/components/TopBanner.css';

const API_URL = 'https://odoo.core.daisies.mx/odoo-firebase-core/odoo-import';
const ACCESS_TOKEN = process.env.REACT_APP_APP_ACCESS_TOKEN;

const DEFAULT_COLOR = '#786ED9';

const DEFAULT_BANNER = {
  name: "🌼📲 Salud Accesible e Integral",
  linkUrl: "https://checkup.daisies.mx",
  linkLabel: "Check-up",
  bannerColor: DEFAULT_COLOR
};

/**
 * TopBanner — se configura automáticamente desde Odoo según el dominio.
 * 
 * Detecta el hostname actual (ej: agenda.daisies.mx) y solicita
 * al backend el banner correspondiente via `get_banner_by_domain`.
 * 
 * Si el backend aún no tiene el método, usa el fallback `get_banners`
 * con el bannerId proporcionado como prop.
 * 
 * @param {number}  [bannerId]  - ID del banner (fallback legacy)
 * @param {function} [onClose]  - Callback al cerrar el banner
 */
const TopBanner = ({ bannerId, onClose }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [banner, setBanner] = useState(DEFAULT_BANNER);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const currentDomain = window.location.hostname;

    const fetchBanner = async () => {
      try {
        // Intentar con el nuevo método get_banner_by_domain
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            params: {
              access_token: ACCESS_TOKEN,
              account_id: 1,
              auth_token: "",
              res_model: "dais.banner",
              res_method: "get_banner_by_domain",
              res_params: { domain: currentDomain },
              res_ids: []
            }
          })
        });

        const data = await response.json();

        if (data.result && data.result.success && data.result.banner) {
          const b = data.result.banner;
          setBanner({
            name: b.description || b.name || DEFAULT_BANNER.name,
            linkUrl: b.link_url || DEFAULT_BANNER.linkUrl,
            linkLabel: b.link_label || DEFAULT_BANNER.linkLabel,
            bannerColor: b.banner_color || DEFAULT_COLOR
          });
          setLoading(false);
          return;
        }

        // Si no hay banner para este dominio, intentar fallback con get_banners
        if (data.result && data.result.success && !data.result.banner) {
          await fetchBannerFallback();
          return;
        }

        // Si el método no existe (error), intentar fallback legacy
        await fetchBannerFallback();
      } catch (error) {
        console.warn('get_banner_by_domain no disponible, intentando fallback...', error);
        await fetchBannerFallback();
      }
    };

    const fetchBannerFallback = async () => {
      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            params: {
              access_token: ACCESS_TOKEN,
              account_id: 1,
              auth_token: "",
              res_model: "dais.banner",
              res_method: "get_banners",
              res_params: {},
              res_ids: bannerId ? [bannerId] : []
            }
          })
        });

        const data = await response.json();

        if (data.result && data.result.success && data.result.banners && data.result.banners.length > 0) {
          const selectedBanner = bannerId
            ? (data.result.banners.find(b => b.id === bannerId) || data.result.banners[0])
            : data.result.banners[0];

          setBanner({
            name: selectedBanner.description || selectedBanner.name || DEFAULT_BANNER.name,
            linkUrl: selectedBanner.link_url || DEFAULT_BANNER.linkUrl,
            linkLabel: selectedBanner.link_label || DEFAULT_BANNER.linkLabel,
            bannerColor: selectedBanner.banner_color || DEFAULT_COLOR
          });
        }
      } catch (error) {
        console.error('Error fetching banner (fallback):', error);
      }
      setLoading(false);
    };

    fetchBanner();
  }, [bannerId]);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) return null;

  const bannerStyle = { backgroundColor: banner.bannerColor };
  const btnStyle = { color: banner.bannerColor };

  return (
    <div className="top-banner" style={bannerStyle}>
      <div className="top-banner__text-container">
        <span className='top-banner__text'>{banner.name}</span>
      </div>
      {banner.linkUrl && (
        <a className="banner-btn" href={banner.linkUrl} target="_blank" rel="noopener noreferrer" style={btnStyle}>
          {banner.linkLabel}
        </a>
      )}
      <button className="banner-close" onClick={handleClose}>×</button>
    </div>
  );
};

export default TopBanner;